import React, { FC, createContext, ReactNode, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Timeframe, getPresetRange } from '@epoch-components/DateRangePicker/utils/presets';
import { Community } from 'types/communities.types';

import useCommunitySummaryMetrics, {
  CommunitySummaryMetricsState
} from '../hooks/useCommunitySummaryMetrics';
import useCommunityPerformanceTable, {
  CommunityPerformanceTableState
} from '../hooks/useCommunityPerformanceTable';

export interface CommunityInsightsContextState {
  summaryMetrics: CommunitySummaryMetricsState;
  performanceTable: CommunityPerformanceTableState;
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    timeframe: Timeframe | null;
    allTimeRange?: { firstEventDate: string; lastEventDate: string } | null;
    updateDateRange: (options: {
      range: { from: Date; to: Date };
      timeframe: Timeframe | null;
    }) => void;
  };
}

// Define default values for filters
const defaultTimeframe: Timeframe = Timeframe.thisWeek;
const defaultRange = getPresetRange(defaultTimeframe);
const defaultFilters = {
  startDate: defaultRange.from,
  endDate: defaultRange.to,
  timeframe: defaultTimeframe,
  updateDateRange: ({
    range,
    timeframe
  }: {
    range: { from: Date; to: Date };
    timeframe: Timeframe | null;
  }): void => {
    // Will update if we save views for leaders
    console.info('New date range:', range, 'New timeframe:', timeframe);
  }
};

const defaultSummaryMetrics: CommunitySummaryMetricsState = {
  currentMetrics: { totalMembers: 0, newMembers: 0, totalAttendance: 0 },
  trends: {
    totalMembers: { isPositive: null, value: null },
    newMembers: { isPositive: null, value: null },
    totalAttendance: { isPositive: null, value: null }
  },
  comparisonText: '',
  loading: true,
  refetch: () => {
    // no-op
  }
};

const defaultPerformanceTable: CommunityPerformanceTableState = {
  dataTotal: 0,
  dataCurrentPage: 1,
  dataResultsPerPage: 10,
  loading: true,
  refetch: () => {
    // no-op
  },
  setSortBy: () => {
    // no-op
  },
  setSortOrder: () => {
    // no-op
  },
  handleChangePage: () => {
    // no-op
  }
};

const defaultContextValue: CommunityInsightsContextState = {
  summaryMetrics: defaultSummaryMetrics,
  performanceTable: defaultPerformanceTable,
  filters: { ...defaultFilters, allTimeRange: null }
};

const CommunityInsightsContext = createContext<CommunityInsightsContextState>(defaultContextValue);

interface FiltersState {
  startDate: Date | null;
  endDate: Date | null;
  timeframe: Timeframe | null;
}

export const CommunityInsightsProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<FiltersState>({
    startDate: defaultRange.from,
    endDate: defaultRange.to,
    timeframe: defaultTimeframe
  });

  const updateDateRange = useCallback(
    ({
      range,
      timeframe
    }: {
      range: { from: Date; to: Date };
      timeframe: Timeframe | null;
    }): void => {
      setFilters({
        startDate: range.from,
        endDate: range.to,
        timeframe
      });
    },
    []
  );

  // Extract the community from the outlet context so that we always work with the current community.
  const { community } = useOutletContext<{ community: Community }>();
  if (!community) {
    throw new Error('Community data is missing in the outlet context.');
  }
  const communityId = community.id;

  const summaryMetrics = useCommunitySummaryMetrics({
    communityId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    timeframe: filters.timeframe
  });

  const performanceTable = useCommunityPerformanceTable({
    communityId,
    startDate: filters.startDate,
    endDate: filters.endDate
  });

  const contextValue = useMemo(
    () => ({
      summaryMetrics,
      performanceTable,

      filters: {
        ...filters,
        updateDateRange,
        allTimeRange: {
          firstEventDate: '2000-01-01',
          lastEventDate: new Date().toISOString().split('T')[0]
        }
      }
    }),
    [summaryMetrics, performanceTable, filters, updateDateRange]
  );

  return (
    <CommunityInsightsContext.Provider value={contextValue}>
      {children}
    </CommunityInsightsContext.Provider>
  );
};

export default CommunityInsightsContext;
